import {
  ArrowLeftRight,
  BarChart3,
  BrainCircuit,
  ChevronDown,
  ChevronRight,
  Clock,
  Database,
  Globe,
  DoorOpen,
  KeyRound,
  Layers,
  Loader2,
  LockKeyhole,
  MessageCircle,
  Plug,
  Plus,
  ServerCog,
  ShieldCheck,
  Sparkles,
  Users,
  Vault,
  Workflow,
  AppWindow,
  Zap,
} from "lucide-react";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { useContextStore } from "@store/context";
import { useAuth } from "@/providers/AuthProvider";
import { usePermissions } from "@/hooks/usePermissions";
import { useAgentSpecTreeResources } from "@/hooks/useAgentSpecTreeResources";
import { cn } from "@/utils/cn";
import {
  routePosture,
  shellModeFromPosture,
} from "@/utils/posture";
import {
  applicationConfigHref,
  applicationReviewHref,
  builderAgentSpecHref,
  builderCalistoHref,
  operateSurfaceHref,
} from "@/utils/contextUrl";
import { filterPlatformMenuItems } from "@/utils/platformMenu";
import {
  ORG_ROUTES,
  RUN_SPACES_ROUTE,
  orgLeafHref,
} from "@/components/organization/orgRoutes";
import {
  AGENT_SETTINGS_ROUTES,
  APPLICATION_WEB_ROUTE,
} from "@/components/build/agentSettingsRoutes";
import {
  leafHrefCommitted,
  lockLeafNavigation,
} from "@/utils/leafNavigationLock";
import CreateApplicationDialog from "@/components/applications/CreateApplicationDialog";
import CreateSandboxAgentDialog from "@/components/studio/CreateSandboxAgentDialog";
import AgentSpecStudioThreadsSidebar, {
  type AgentSpecStudioThreadsSidebarHandle,
} from "@/components/studio/AgentSpecStudioThreadsSidebar";
import {
  applicationsVisibleInRun,
  resolveRunChatAgentId,
} from "@/components/shell/runApplicationAgents";
import {
  TREE_CHILD_INDENT_CLASS,
  groupsToReveal,
  isAgentGroupOpen,
  isTreeLeafCurrent,
  readNavOpenState,
  revealAgentGroups,
  writeNavOpenState,
  type AgentTreeGroup,
} from "@/components/shell/shellNavTreeState";

type IconType = ComponentType<{ size?: number; className?: string }>;

type LeafItem = {
  id: string;
  label: string;
  href: string;
  icon: IconType;
  busy?: boolean;
  onSelect?: () => void;
};

interface ShellNavTreeProps {
  expanded: boolean;
  mobile?: boolean;
  onNavigate?: () => void;
}

function TreeChildren({ children }: { children: ReactNode }) {
  return <div className={TREE_CHILD_INDENT_CLASS}>{children}</div>;
}

function TreeCreateButton({
  label,
  onCreate,
  testId,
}: {
  label: string;
  onCreate: () => void;
  testId?: string;
}) {
  return (
    <button
      type="button"
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onCreate();
      }}
      aria-label={label}
      title={label}
      data-testid={testId}
      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-sm text-muted-foreground hover:bg-card-raised hover:text-foreground"
    >
      <Plus size={14} />
    </button>
  );
}

function TreeSection({
  title,
  open,
  onToggle,
  expanded,
  children,
  onCreate,
  createLabel,
  createTestId,
}: {
  title: string;
  open: boolean;
  onToggle: () => void;
  expanded: boolean;
  children: ReactNode;
  onCreate?: () => void;
  createLabel?: string;
  createTestId?: string;
}) {
  const createButton =
    onCreate && createLabel ? (
      <TreeCreateButton
        label={createLabel}
        onCreate={onCreate}
        testId={createTestId}
      />
    ) : null;

  if (!expanded) {
    return (
      <div className="space-y-1">
        {createButton ? (
          <div className="flex justify-center">{createButton}</div>
        ) : null}
        {children}
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-0.5">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          className="flex min-w-0 flex-1 items-center gap-2 rounded-md px-2 py-1.5 text-[13px] font-semibold text-foreground hover:bg-card-raised"
        >
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          <span className="truncate">{title}</span>
        </button>
        {createButton}
      </div>
      {open ? <TreeChildren>{children}</TreeChildren> : null}
    </div>
  );
}

function TreeToggle({
  open,
  label,
  onToggle,
  testId,
}: {
  open: boolean;
  label: string;
  onToggle: () => void;
  testId?: string;
}) {
  return (
    <button
      type="button"
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onToggle();
      }}
      aria-expanded={open}
      aria-label={label}
      data-testid={testId}
      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-sm text-muted-foreground hover:bg-card-raised hover:text-foreground"
    >
      {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
    </button>
  );
}

function TreeBranch({
  label,
  open,
  onToggle,
  active,
  expanded,
  icon: Icon,
  testId,
  href,
  onNavigate,
  onSelect,
  children,
  onCreate,
  createLabel,
  createTestId,
}: {
  label: string;
  open: boolean;
  onToggle: () => void;
  active?: boolean;
  expanded: boolean;
  icon: IconType;
  testId?: string;
  href?: string;
  onNavigate?: () => void;
  onSelect?: () => void;
  children: ReactNode;
  onCreate?: () => void;
  createLabel?: string;
  createTestId?: string;
}) {
  const rowClass = cn(
    "flex min-w-0 flex-1 items-center gap-2 rounded-md py-1.5 text-sm transition-colors",
    expanded ? "px-0" : "justify-center px-2",
    active
      ? "bg-primary-soft font-semibold text-foreground"
      : "text-foreground hover:bg-card-raised hover:text-foreground",
  );
  const body = (
    <>
      <Icon size={18} className="shrink-0" />
      {expanded ? <span className="truncate">{label}</span> : null}
    </>
  );

  return (
    <div className="space-y-0.5">
      <div className="flex items-center">
        {expanded && href ? (
          <TreeToggle
            open={open}
            label={label}
            onToggle={onToggle}
            testId={`${testId}-toggle`}
          />
        ) : null}
        {href ? (
          <Link
            to={href}
            onClick={() => {
              onNavigate?.();
              lockLeafNavigation();
              adoptLeafSelection(href, onSelect);
            }}
            className={rowClass}
            data-testid={testId}
            aria-current={active ? "page" : undefined}
            title={!expanded ? label : undefined}
          >
            {body}
          </Link>
        ) : (
          <button
            type="button"
            onClick={onToggle}
            data-testid={testId}
            aria-expanded={open}
            className={rowClass}
            title={!expanded ? label : undefined}
          >
            {expanded ? (
              <span className="flex h-6 w-6 shrink-0 items-center justify-center text-muted-foreground">
                {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              </span>
            ) : null}
            {body}
          </button>
        )}
        {expanded && onCreate && createLabel ? (
          <TreeCreateButton
            label={createLabel}
            onCreate={onCreate}
            testId={createTestId}
          />
        ) : null}
      </div>
      {open ? <TreeChildren>{children}</TreeChildren> : null}
    </div>
  );
}

function TreeFolder({
  label,
  open,
  onToggle,
  expanded,
  icon: Icon,
  testId,
  href,
  onNavigate,
  onSelect,
  active,
  count,
  children,
  onCreate,
  createLabel,
  createTestId,
}: {
  label: string;
  open: boolean;
  onToggle: () => void;
  expanded: boolean;
  icon?: IconType;
  testId?: string;
  href?: string;
  onNavigate?: () => void;
  onSelect?: () => void;
  active?: boolean;
  count?: number;
  children?: ReactNode;
  onCreate?: () => void;
  createLabel?: string;
  createTestId?: string;
}) {
  const canFold = Boolean(children);
  if (!expanded) {
    if (!href) {
      return null;
    }
    return (
      <LeafLink
        item={{
          id: testId?.replace(/^shell-nav-/, "") ?? label,
          label,
          href,
          icon: Icon ?? Sparkles,
          onSelect,
        }}
        active={Boolean(active)}
        expanded={false}
        onNavigate={onNavigate}
      />
    );
  }

  const rowClass = cn(
    "flex min-w-0 flex-1 items-center gap-2 rounded-md py-1.5 text-sm transition-colors",
    active
      ? "bg-primary-soft font-semibold text-foreground"
      : "text-foreground hover:bg-card-raised hover:text-foreground",
  );
  const body = (
    <>
      {Icon ? <Icon size={16} className="shrink-0" /> : null}
      <span className="truncate">{label}</span>
      {count != null ? (
        <span className="ml-auto pr-1 tabular-nums text-[10px] text-muted-foreground">
          {count}
        </span>
      ) : null}
    </>
  );

  return (
    <div className="space-y-0.5">
      <div className="flex items-center">
        {canFold ? (
          <TreeToggle
            open={open}
            label={label}
            onToggle={onToggle}
            testId={href ? `${testId}-toggle` : testId}
          />
        ) : (
          <span className="inline-block h-6 w-6 shrink-0" />
        )}
        {href ? (
          <Link
            to={href}
            onClick={() => {
              onNavigate?.();
              lockLeafNavigation();
              adoptLeafSelection(href, onSelect);
            }}
            className={rowClass}
            data-testid={testId}
            aria-current={active ? "page" : undefined}
          >
            {body}
          </Link>
        ) : (
          <button type="button" onClick={onToggle} className={rowClass}>
            {body}
          </button>
        )}
        {onCreate && createLabel ? (
          <TreeCreateButton
            label={createLabel}
            onCreate={onCreate}
            testId={createTestId}
          />
        ) : null}
      </div>
      {canFold && open ? <TreeChildren>{children}</TreeChildren> : null}
    </div>
  );
}

function adoptLeafSelection(href: string, onSelect?: () => void) {
  if (!onSelect) {
    return;
  }
  const started = Date.now();
  const tryAdopt = () => {
    // Wait for path AND agentId. Same-path chat switches used to look
    // "ready" immediately and selectAgent cancelled the in-flight query.
    if (leafHrefCommitted(href)) {
      onSelect();
      return;
    }
    if (Date.now() - started < 2000) {
      window.requestAnimationFrame(tryAdopt);
    }
  };
  window.requestAnimationFrame(tryAdopt);
}

function LeafLink({
  item,
  active,
  expanded,
  onNavigate,
  nested,
  alignToggle,
}: {
  item: LeafItem;
  active: boolean;
  expanded: boolean;
  onNavigate?: () => void;
  nested?: boolean;
  alignToggle?: boolean;
}) {
  const Icon = item.icon;
  return (
    <Link
      to={item.href}
      onClick={() => {
        // Let the <Link> commit first. Updating the context store in the
        // same click used to make ContextProvider replace-navigate the
        // still-current /studio/chat URL and cancel the leaf.
        onNavigate?.();
        lockLeafNavigation();
        adoptLeafSelection(item.href, item.onSelect);
      }}
      className={cn(
        "flex items-center gap-2 rounded-md py-1.5 text-sm transition-colors",
        expanded ? "px-0" : "justify-center px-2",
        nested && "text-xs",
        active
          ? "bg-primary-soft font-semibold text-foreground"
          : "text-foreground hover:bg-card-raised hover:text-foreground",
      )}
      title={!expanded ? item.label : undefined}
      data-testid={`shell-nav-${item.id}`}
      aria-current={active ? "page" : undefined}
      aria-busy={item.busy || undefined}
    >
      {expanded && alignToggle ? (
        <span className="inline-block h-6 w-6 shrink-0" />
      ) : null}
      {item.busy ? (
        <Loader2
          size={nested ? 14 : 16}
          className="shrink-0 animate-spin motion-reduce:animate-none"
        />
      ) : (
        <Icon size={nested ? 14 : 16} className="shrink-0" />
      )}
      {expanded ? <span className="truncate">{item.label}</span> : null}
    </Link>
  );
}

function AgentSpecTreeSection({
  organizationId,
  agentId,
  open,
  expanded,
  agentActive,
  activeSection,
  selectedResourceId,
  specOpen,
  workflowsOpen,
  miniappsOpen,
  onToggleSpec,
  onToggleWorkflows,
  onToggleMiniapps,
  selectAgent,
  onNavigate,
}: {
  organizationId: string | null;
  agentId: string;
  open: boolean;
  expanded: boolean;
  agentActive: boolean;
  activeSection: string | null;
  selectedResourceId: string | null;
  specOpen: boolean;
  workflowsOpen: boolean;
  miniappsOpen: boolean;
  onToggleSpec: () => void;
  onToggleWorkflows: () => void;
  onToggleMiniapps: () => void;
  selectAgent: (agentId: string) => void;
  onNavigate?: () => void;
}) {
  const { t } = useTranslation();
  const resources = useAgentSpecTreeResources(organizationId, agentId, open);
  const overviewHref = builderAgentSpecHref(organizationId, agentId, {
    section: "overview",
    threadId: resources.threadId,
  });
  const workflowsHref = builderAgentSpecHref(organizationId, agentId, {
    section: "workflows",
    resourceId: resources.workflows[0]?.id,
    threadId: resources.threadId,
  });
  const miniappsHref = builderAgentSpecHref(organizationId, agentId, {
    section: "miniapps",
    resourceId: resources.miniapps[0]?.id,
    threadId: resources.threadId,
  });

  return (
    <TreeFolder
      label={t("agentSpec.navGroup")}
      open={specOpen}
      onToggle={onToggleSpec}
      expanded={expanded}
      testId={`shell-nav-studio-agent-${agentId}-group-spec`}
    >
      <LeafLink
        item={{
          id: `studio-agent-${agentId}`,
          label: t("agentSpec.agentOverview"),
          href: overviewHref,
          icon: Sparkles,
          onSelect: () => selectAgent(agentId),
        }}
        active={
          isTreeLeafCurrent({
            selfIsDestination:
              agentActive &&
              // Executions and Plans are tabs of the Overview page, not
              // tree nodes (lot R4): the Overview leaf stays current.
              (activeSection === "overview" ||
                activeSection === "plans" ||
                activeSection === "executions" ||
                !activeSection),
          })
        }
        expanded={expanded}
        onNavigate={onNavigate}
        alignToggle
      />
      <TreeFolder
        label={t("agentSpec.workflowsTitle")}
        open={workflowsOpen}
        onToggle={onToggleWorkflows}
        expanded={expanded}
        icon={Workflow}
        testId={`shell-nav-studio-agent-${agentId}-workflows`}
        href={workflowsHref}
        onSelect={() => selectAgent(agentId)}
        onNavigate={onNavigate}
        active={isTreeLeafCurrent({
          selfIsDestination: agentActive && activeSection === "workflows",
          descendantIsDestination: Boolean(selectedResourceId),
        })}
        count={resources.workflows.length}
      >
        {resources.workflows.map((resource) => (
          <LeafLink
            key={`wf-${resource.id}`}
            item={{
              id: `studio-agent-${agentId}-wf-${resource.id}`,
              label: resource.title,
              href: builderAgentSpecHref(organizationId, agentId, {
                section: "workflows",
                resourceId: resource.id,
                threadId: resources.threadId,
              }),
              icon: Workflow,
              onSelect: () => selectAgent(agentId),
            }}
            active={
              agentActive &&
              activeSection === "workflows" &&
              selectedResourceId === resource.id
            }
            expanded={expanded}
            onNavigate={onNavigate}
            nested
            alignToggle
          />
        ))}
      </TreeFolder>
      <TreeFolder
        label={t("agentSpec.miniappsTitle")}
        open={miniappsOpen}
        onToggle={onToggleMiniapps}
        expanded={expanded}
        icon={AppWindow}
        testId={`shell-nav-studio-agent-${agentId}-miniapps`}
        href={miniappsHref}
        onSelect={() => selectAgent(agentId)}
        onNavigate={onNavigate}
        active={isTreeLeafCurrent({
          selfIsDestination: agentActive && activeSection === "miniapps",
          descendantIsDestination: Boolean(selectedResourceId),
        })}
        count={resources.miniapps.length}
      >
        {resources.miniapps.map((resource) => (
          <LeafLink
            key={`ma-${resource.id}`}
            item={{
              id: `studio-agent-${agentId}-ma-${resource.id}`,
              label: resource.title,
              href: builderAgentSpecHref(organizationId, agentId, {
                section: "miniapps",
                resourceId: resource.id,
                threadId: resources.threadId,
              }),
              icon: AppWindow,
              onSelect: () => selectAgent(agentId),
            }}
            active={
              agentActive &&
              activeSection === "miniapps" &&
              selectedResourceId === resource.id
            }
            expanded={expanded}
            onNavigate={onNavigate}
            nested
            alignToggle
          />
        ))}
      </TreeFolder>
    </TreeFolder>
  );
}

export default function ShellNavTree({
  expanded,
  mobile = false,
  onNavigate,
}: ShellNavTreeProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const posture = routePosture(location.pathname);
  const mode = shellModeFromPosture(posture);
  const { canRead, canWrite, canDeploy, canManageUsers } = usePermissions();
  const { user } = useAuth();

  const org = useContextStore((state) => state.org);
  const applications = useContextStore((state) => state.applications);
  const deployments = useContextStore((state) => state.deployments);
  const chiefOfStaff = useContextStore((state) => state.chiefOfStaff);
  const applicationKey = useContextStore((state) => state.applicationKey);
  const deploymentId = useContextStore((state) => state.deploymentId);
  const setApplicationKey = useContextStore((state) => state.setApplicationKey);
  const setDeploymentId = useContextStore((state) => state.setDeploymentId);
  const [createApplicationOpen, setCreateApplicationOpen] = useState(false);
  const [createAgentOpen, setCreateAgentOpen] = useState(false);
  const cosThreadsRef = useRef<AgentSpecStudioThreadsSidebarHandle>(null);
  const agentThreadRefs = useRef<
    Record<string, AgentSpecStudioThreadsSidebarHandle | null>
  >({});

  const initialOpen = useMemo(() => readNavOpenState(), []);
  const [appsOpen, setAppsOpen] = useState(initialOpen.appsOpen);
  const [agentsOpen, setAgentsOpen] = useState(initialOpen.agentsOpen);
  const [workOpen, setWorkOpen] = useState(initialOpen.workOpen);
  const [orgOpen, setOrgOpen] = useState(initialOpen.orgOpen);
  const [openAppKeys, setOpenAppKeys] = useState(initialOpen.openAppKeys);
  const [openAgentIds, setOpenAgentIds] = useState(initialOpen.openAgentIds);
  const [openAgentGroups, setOpenAgentGroups] = useState(
    initialOpen.openAgentGroups,
  );

  useEffect(() => {
    writeNavOpenState({
      appsOpen,
      agentsOpen,
      workOpen,
      orgOpen,
      openAppKeys,
      openAgentIds,
      openAgentGroups,
    });
  }, [
    appsOpen,
    agentsOpen,
    workOpen,
    orgOpen,
    openAppKeys,
    openAgentIds,
    openAgentGroups,
  ]);

  // Soft expand only the newly selected agent/app once — never force-open all branches.
  useEffect(() => {
    if (!deploymentId) return;
    setOpenAgentIds((current) =>
      current[deploymentId] ? current : { ...current, [deploymentId]: true },
    );
  }, [deploymentId]);

  useEffect(() => {
    if (!applicationKey) return;
    setOpenAppKeys((current) =>
      current[applicationKey]
        ? current
        : { ...current, [applicationKey]: true },
    );
  }, [applicationKey]);

  const closeMobile = () => {
    if (mobile) {
      onNavigate?.();
    }
  };

  const selectApplication = (key: string, chatAgentId?: string) => {
    setApplicationKey(key);
    localStorage.setItem("agent-os.current-application-key", key);
    if (chatAgentId) {
      setDeploymentId(chatAgentId);
    }
  };

  const selectAgent = (agentId: string) => {
    setApplicationKey(null);
    localStorage.removeItem("agent-os.current-application-key");
    setDeploymentId(agentId);
  };

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const selectedResourceId = searchParams.get("resourceId");
  const activeSection = searchParams.get("section");

  useEffect(() => {
    if (!deploymentId) return;
    const groups = groupsToReveal({
      pathname: location.pathname,
      activeSection,
    });
    if (groups.length === 0) return;
    setOpenAgentGroups((current) =>
      revealAgentGroups(current, deploymentId, groups),
    );
  }, [activeSection, deploymentId, location.pathname]);

  const toggleAgentGroup = (agentId: string, group: AgentTreeGroup) => {
    setOpenAgentGroups((current) => ({
      ...current,
      [`${agentId}:${group}`]: !isAgentGroupOpen(current, agentId, group),
    }));
  };

  const runLeavesForApp = (appKey: string, chatAgentId?: string): LeafItem[] =>
    (
      [
        {
          id: `run-${appKey}-chat`,
          label: t("sidebar.chat"),
          href: operateSurfaceHref("/run/chat", {
            organizationId: org,
            applicationKey: appKey,
            agentId: chatAgentId,
          }),
          icon: Zap,
          onSelect: () => selectApplication(appKey, chatAgentId),
        },
        {
          id: `run-${appKey}-catalog`,
          label: t("sidebar.catalog"),
          href: operateSurfaceHref("/run/catalog", {
            organizationId: org,
            applicationKey: appKey,
            agentId: chatAgentId,
          }),
          icon: BarChart3,
          onSelect: () => selectApplication(appKey, chatAgentId),
        },
        {
          id: `run-${appKey}-history`,
          label: t("sidebar.history"),
          href: operateSurfaceHref("/run/history", {
            organizationId: org,
            applicationKey: appKey,
            agentId: chatAgentId,
          }),
          icon: Clock,
          onSelect: () => selectApplication(appKey, chatAgentId),
        },
        // Spaces is an expert hatch inside the Application, the single Run
        // entry for spaces (lot R4, OSR-12).
        {
          id: `run-${appKey}-spaces`,
          label: t("settings.runSpaces.navLabel"),
          href: operateSurfaceHref(RUN_SPACES_ROUTE, {
            organizationId: org,
            applicationKey: appKey,
            agentId: chatAgentId,
          }),
          icon: Users,
          onSelect: () => selectApplication(appKey, chatAgentId),
        },
      ] as LeafItem[]
    ).filter((item) => {
      if (
        item.id.endsWith("-history") ||
        item.id.endsWith("-chat") ||
        item.id.endsWith("-spaces")
      ) {
        return canRead("executions");
      }
      return canRead("recipes");
    });

  const enterStudioForAgent = (agentId: string, applicationKeyValue?: string) => {
    if (applicationKeyValue) {
      setApplicationKey(applicationKeyValue);
      localStorage.setItem("agent-os.current-application-key", applicationKeyValue);
    } else {
      setApplicationKey(null);
      localStorage.removeItem("agent-os.current-application-key");
    }
    setDeploymentId(agentId);
  };

  // Studio › Organisation (design/os-reorganisation-ecrans § 3, lot R3):
  // org-level leaves only — Application keys stay under Access, Agent keys
  // and Agent models under the Agent.
  const orgAgentParams: Record<string, string> = deploymentId
    ? { agentId: deploymentId, deploymentId }
    : {};
  const organizationLeaves: LeafItem[] = [
    ...filterPlatformMenuItems({
      canDeploy: canDeploy(),
      canWriteRecipes: canWrite("recipes"),
    }).map((item) => ({
      id: item.id === "platform-llm" ? "studio-models" : `studio-${item.id}`,
      label: t(item.labelKey, { defaultValue: item.defaultLabel }),
      href: orgLeafHref(item.href, org),
      icon: item.icon,
    })),
    ...(canManageUsers()
      ? ([
          {
            id: "studio-org-access-map",
            label: t("sidebar.orgAccessMap"),
            href: orgLeafHref(ORG_ROUTES.accessMap, org),
            icon: ShieldCheck,
          },
          {
            id: "studio-org-gates",
            label: t("sidebar.orgGates"),
            href: orgLeafHref(ORG_ROUTES.gates, org, orgAgentParams),
            icon: DoorOpen,
          },
          {
            id: "studio-org-members",
            label: t("sidebar.orgMembers"),
            href: orgLeafHref(ORG_ROUTES.members, org),
            icon: Users,
          },
          {
            id: "studio-org-vault",
            label: t("sidebar.orgVault"),
            href: orgLeafHref(ORG_ROUTES.vault, org),
            icon: Vault,
          },
        ] as LeafItem[])
      : []),
    ...(canDeploy() || canManageUsers()
      ? ([
          {
            id: "studio-org-usage",
            label: t("sidebar.orgUsage"),
            href: orgLeafHref(ORG_ROUTES.usage, org),
            icon: BarChart3,
          },
        ] as LeafItem[])
      : []),
    ...(canDeploy()
      ? ([
          {
            id: "studio-org-import-export",
            label: t("sidebar.orgImportExport"),
            href: orgLeafHref(ORG_ROUTES.importExport, org, orgAgentParams),
            icon: ArrowLeftRight,
          },
        ] as LeafItem[])
      : []),
  ];

  const onAgentSpecSurface = location.pathname.startsWith("/studio/agent-spec");
  const isActivePath = (pathname: string) =>
    location.pathname === pathname ||
    location.pathname.startsWith(`${pathname}/`);

  const runApplications = useMemo(
    () => applicationsVisibleInRun(applications, user?.id),
    [applications, user?.id],
  );
  const travailLeaves: LeafItem[] = [
    {
      id: "run-travail-inbox",
      label: t("sidebar.travailInbox"),
      href: operateSurfaceHref("/run/travail/inbox", {
        organizationId: org,
        applicationKey,
        agentId: deploymentId,
      }),
      icon: Clock,
    },
    {
      id: "run-travail-activity",
      label: t("sidebar.travailActivity"),
      href: operateSurfaceHref("/run/travail/activity", {
        organizationId: org,
        applicationKey,
        agentId: deploymentId,
      }),
      icon: BarChart3,
    },
  ];

  if (mode === "run") {
    return (
      <div className="space-y-2" data-testid="shell-nav-run">
        <TreeSection
          title={t("sidebar.applications")}
          open={appsOpen}
          onToggle={() => setAppsOpen((value) => !value)}
          expanded={expanded}
          onCreate={
            canDeploy()
              ? () => {
                  setCreateApplicationOpen(true);
                  closeMobile();
                }
              : undefined
          }
          createLabel={t("contextSwitcher.createApplicationTitle")}
          createTestId="shell-nav-run-app-create"
        >
          {runApplications.length === 0 ? (
            <div className="px-2 py-1 text-xs text-muted-foreground">
              {t("sidebar.noApplications")}
            </div>
          ) : (
            runApplications.map((app) => {
              const chatAgentId = resolveRunChatAgentId({
                app,
                deployments,
                selectedAgentId:
                  applicationKey === app.key ? deploymentId : null,
              });
              const open = openAppKeys[app.key] ?? false;
              const leaves = runLeavesForApp(app.key, chatAgentId || undefined);
              return (
                <TreeBranch
                  key={app.key}
                  label={app.display_name || app.key}
                  open={Boolean(open)}
                  expanded={expanded}
                  icon={Layers}
                  testId={`shell-nav-run-app-${app.key}`}
                  onToggle={() => {
                    setOpenAppKeys((current) => ({
                      ...current,
                      [app.key]: !open,
                    }));
                  }}
                >
                  {leaves.map((leaf) => (
                    <LeafLink
                      key={leaf.id}
                      item={leaf}
                      active={
                        applicationKey === app.key &&
                        isActivePath(leaf.href.split("?")[0])
                      }
                      expanded={expanded}
                      onNavigate={closeMobile}
                    />
                  ))}
                </TreeBranch>
              );
            })
          )}
        </TreeSection>
        {canRead("executions") ? (
          <TreeSection
            title={t("sidebar.travail")}
            open={workOpen}
            onToggle={() => setWorkOpen((value) => !value)}
            expanded={expanded}
          >
            {travailLeaves.map((leaf) => (
              <LeafLink
                key={leaf.id}
                item={leaf}
                active={isActivePath(leaf.href.split("?")[0])}
                expanded={expanded}
                onNavigate={closeMobile}
              />
            ))}
          </TreeSection>
        ) : null}
        {org ? (
          <CreateApplicationDialog
            open={createApplicationOpen}
            onOpenChange={setCreateApplicationOpen}
            organizationId={org}
          />
        ) : null}
      </div>
    );
  }

  const showStudioChat = canWrite("recipes");
  const showStudioNode = canRead("recipes") || showStudioChat;

  return (
    <div className="space-y-3" data-testid="shell-nav-studio">
      {chiefOfStaff && showStudioNode ? (
        <TreeBranch
          label={t("sidebar.chiefOfStaff")}
          open={Boolean(openAgentIds[chiefOfStaff.id])}
          expanded={expanded}
          icon={Sparkles}
          testId="shell-nav-studio-chief-of-staff"
          href={builderCalistoHref(
            org,
            chiefOfStaff.id,
            deploymentId === chiefOfStaff.id
              ? { threadId: searchParams.get("threadId") }
              : undefined,
          )}
          onSelect={() => {
            enterStudioForAgent(chiefOfStaff.id);
            setOpenAgentIds((current) => ({
              ...current,
              [chiefOfStaff.id]: true,
            }));
          }}
          onNavigate={closeMobile}
          onToggle={() => {
            setOpenAgentIds((current) => ({
              ...current,
              [chiefOfStaff.id]: !current[chiefOfStaff.id],
            }));
          }}
        >
          <TreeFolder
            label={t("sidebar.agentStudio")}
            open={isAgentGroupOpen(
              openAgentGroups,
              chiefOfStaff.id,
              "studio",
            )}
            onToggle={() => toggleAgentGroup(chiefOfStaff.id, "studio")}
            expanded={expanded}
            testId="shell-nav-studio-chief-of-staff-group-studio"
            href={builderCalistoHref(org, chiefOfStaff.id)}
            onSelect={() => enterStudioForAgent(chiefOfStaff.id)}
            onNavigate={closeMobile}
            active={isTreeLeafCurrent({
              selfIsDestination:
                deploymentId === chiefOfStaff.id &&
                location.pathname.startsWith("/studio/chat") &&
                !searchParams.get("threadId"),
              descendantIsDestination: Boolean(
                deploymentId === chiefOfStaff.id &&
                  searchParams.get("threadId"),
              ),
            })}
            onCreate={() => cosThreadsRef.current?.startNewThread()}
            createLabel={t("agentSpec.studioCreateThread")}
            createTestId={`shell-nav-studio-agent-${chiefOfStaff.id}-new-thread`}
          >
            <AgentSpecStudioThreadsSidebar
              ref={cosThreadsRef}
              hideNewThreadRow
              expanded={expanded}
              agentId={chiefOfStaff.id}
              onEnterStudio={enterStudioForAgent}
              onNavigate={mobile ? onNavigate : undefined}
            />
          </TreeFolder>
        </TreeBranch>
      ) : null}
      <TreeSection
        title={t("sidebar.deployments")}
        open={agentsOpen}
        onToggle={() => setAgentsOpen((value) => !value)}
        expanded={expanded}
        onCreate={
          canWrite("recipes")
            ? () => {
                setCreateAgentOpen(true);
                closeMobile();
              }
            : undefined
        }
        createLabel={t("contextSwitcher.createDeploymentTitle")}
        createTestId="shell-nav-studio-agent-create"
      >
        {canWrite("recipes")
          ? deployments.map((agent) => {
              const agentActive =
                deploymentId === agent.id && onAgentSpecSurface;
              const open = openAgentIds[agent.id] ?? false;
              const agentConfigHref = (path: string) => {
                const params = new URLSearchParams();
                if (org) params.set("organizationId", org);
                params.set("agentId", agent.id);
                params.set("deploymentId", agent.id);
                return `${path}?${params.toString()}`;
              };
              // Agent settings: one leaf per home, each with its own route
              // (design/os-reorganisation-ecrans § 3, lot R4). Secrets live
              // out of Tools; Agent keys and Spaces belong to the Agent.
              const configLeaves: LeafItem[] = [
                {
                  id: `studio-agent-${agent.id}-mcp`,
                  label: t("sidebar.tools"),
                  href: agentConfigHref(AGENT_SETTINGS_ROUTES.tools),
                  icon: ServerCog,
                  onSelect: () => selectAgent(agent.id),
                },
                ...(canDeploy()
                  ? ([
                      {
                        id: `studio-agent-${agent.id}-connectors`,
                        label: t("settings.connectors.navLabel"),
                        href: agentConfigHref(AGENT_SETTINGS_ROUTES.connectors),
                        icon: Plug,
                        onSelect: () => selectAgent(agent.id),
                      },
                      {
                        id: `studio-agent-${agent.id}-secrets`,
                        label: t("settings.secrets.navLabel"),
                        href: agentConfigHref(AGENT_SETTINGS_ROUTES.secrets),
                        icon: LockKeyhole,
                        onSelect: () => selectAgent(agent.id),
                      },
                    ] as LeafItem[])
                  : []),
                ...(canWrite("recipes")
                  ? ([
                      {
                        id: `studio-agent-${agent.id}-data`,
                        label: t("sidebar.data"),
                        href: agentConfigHref(AGENT_SETTINGS_ROUTES.data),
                        icon: Database,
                        onSelect: () => selectAgent(agent.id),
                      },
                    ] as LeafItem[])
                  : []),
                ...(canDeploy()
                  ? ([
                      {
                        id: `studio-agent-${agent.id}-models`,
                        label: t("sidebar.agentModels"),
                        href: agentConfigHref(AGENT_SETTINGS_ROUTES.models),
                        icon: BrainCircuit,
                        onSelect: () => selectAgent(agent.id),
                      },
                      {
                        id: `studio-agent-${agent.id}-keys`,
                        label: t("apiKeys.navLabel"),
                        href: agentConfigHref(AGENT_SETTINGS_ROUTES.keys),
                        icon: KeyRound,
                        onSelect: () => selectAgent(agent.id),
                      },
                      {
                        id: `studio-agent-${agent.id}-spaces`,
                        label: t("sidebar.spaces"),
                        href: agentConfigHref(AGENT_SETTINGS_ROUTES.spaces),
                        icon: Users,
                        onSelect: () => selectAgent(agent.id),
                      },
                    ] as LeafItem[])
                  : []),
              ];
              return (
                <TreeBranch
                  key={agent.id}
                  label={agent.name || agent.id}
                  open={Boolean(open)}
                  expanded={expanded}
                  icon={Sparkles}
                  testId={`shell-nav-studio-agent-branch-${agent.id}`}
                  onToggle={() => {
                    setOpenAgentIds((current) => ({
                      ...current,
                      [agent.id]: !open,
                    }));
                  }}
                >
                  {showStudioChat ? (
                    <TreeFolder
                      label={t("sidebar.agentStudio")}
                      open={isAgentGroupOpen(
                        openAgentGroups,
                        agent.id,
                        "studio",
                      )}
                      onToggle={() => toggleAgentGroup(agent.id, "studio")}
                      expanded={expanded}
                      testId={`shell-nav-studio-agent-${agent.id}-group-studio`}
                      onCreate={() =>
                        agentThreadRefs.current[agent.id]?.startNewThread()
                      }
                      createLabel={t("agentSpec.studioCreateThread")}
                      createTestId={`shell-nav-studio-agent-${agent.id}-new-thread`}
                    >
                      <div data-testid={`shell-nav-studio-agent-${agent.id}-studio`}>
                        <AgentSpecStudioThreadsSidebar
                          ref={(handle) => {
                            agentThreadRefs.current[agent.id] = handle;
                          }}
                          hideNewThreadRow
                          expanded={expanded}
                          agentId={agent.id}
                          onEnterStudio={enterStudioForAgent}
                          onNavigate={mobile ? onNavigate : undefined}
                        />
                      </div>
                    </TreeFolder>
                  ) : null}
                  <AgentSpecTreeSection
                    organizationId={org}
                    agentId={agent.id}
                    open={Boolean(open)}
                    expanded={expanded}
                    agentActive={agentActive}
                    activeSection={activeSection}
                    selectedResourceId={selectedResourceId}
                    specOpen={isAgentGroupOpen(
                      openAgentGroups,
                      agent.id,
                      "spec",
                    )}
                    workflowsOpen={isAgentGroupOpen(
                      openAgentGroups,
                      agent.id,
                      "workflows",
                    )}
                    miniappsOpen={isAgentGroupOpen(
                      openAgentGroups,
                      agent.id,
                      "miniapps",
                    )}
                    onToggleSpec={() => toggleAgentGroup(agent.id, "spec")}
                    onToggleWorkflows={() =>
                      toggleAgentGroup(agent.id, "workflows")
                    }
                    onToggleMiniapps={() =>
                      toggleAgentGroup(agent.id, "miniapps")
                    }
                    selectAgent={selectAgent}
                    onNavigate={closeMobile}
                  />
                  {configLeaves.length > 0 ? (
                    <TreeFolder
                      label={t("sidebar.agentConfig")}
                      open={isAgentGroupOpen(
                        openAgentGroups,
                        agent.id,
                        "config",
                      )}
                      onToggle={() => toggleAgentGroup(agent.id, "config")}
                      expanded={expanded}
                      testId={`shell-nav-studio-agent-${agent.id}-group-config`}
                    >
                      {configLeaves.map((leaf) => (
                        <LeafLink
                          key={leaf.id}
                          item={leaf}
                          active={
                            deploymentId === agent.id &&
                            isActivePath(leaf.href.split("?")[0])
                          }
                          expanded={expanded}
                          onNavigate={closeMobile}
                          nested
                          alignToggle
                        />
                      ))}
                    </TreeFolder>
                  ) : null}
                </TreeBranch>
              );
            })
          : null}
        {org ? (
          <CreateSandboxAgentDialog
            open={createAgentOpen}
            onOpenChange={setCreateAgentOpen}
            organizationId={org}
          />
        ) : null}
      </TreeSection>

      <TreeSection
        title={t("sidebar.applications")}
        open={appsOpen}
        onToggle={() => setAppsOpen((value) => !value)}
        expanded={expanded}
        onCreate={
          canDeploy()
            ? () => {
                setCreateApplicationOpen(true);
                closeMobile();
              }
            : undefined
        }
        createLabel={t("contextSwitcher.createApplicationTitle")}
        createTestId="shell-nav-studio-app-create"
      >
        {applications.map((app) => {
          const chatAgentId = app.entrypoints?.chat?.agent_id?.trim();
          const open = openAppKeys[app.key] ?? false;
          const reviewHref = applicationReviewHref(app.id, {
            organizationId: org,
            applicationKey: app.key,
            agentId: chatAgentId,
          });
          const appLeaves: LeafItem[] = [
            {
              id: `studio-app-${app.key}-review`,
              label: t("sidebar.applicationReview"),
              href: reviewHref,
              icon: Layers,
              onSelect: () => selectApplication(app.key, chatAgentId),
            },
            ...(canDeploy() || canManageUsers()
              ? ([
                  {
                    id: `studio-app-${app.key}-access`,
                    label: t("sidebar.access"),
                    href: applicationConfigHref("/studio/access", {
                      organizationId: org,
                      applicationKey: app.key,
                      agentId: chatAgentId,
                    }),
                    icon: KeyRound,
                    onSelect: () => selectApplication(app.key, chatAgentId),
                  },
                ] as LeafItem[])
              : []),
            ...(canDeploy()
              ? ([
                  {
                    id: `studio-app-${app.key}-channels`,
                    label: t("sidebar.channels"),
                    href: applicationConfigHref("/studio/messaging", {
                      organizationId: org,
                      applicationKey: app.key,
                      agentId: chatAgentId,
                    }),
                    icon: MessageCircle,
                    onSelect: () => selectApplication(app.key, chatAgentId),
                  },
                  {
                    id: `studio-app-${app.key}-web`,
                    label: t("settings.web.navLabel"),
                    href: applicationConfigHref(APPLICATION_WEB_ROUTE, {
                      organizationId: org,
                      applicationKey: app.key,
                      agentId: chatAgentId,
                    }),
                    icon: Globe,
                    onSelect: () => selectApplication(app.key, chatAgentId),
                  },
                ] as LeafItem[])
              : []),
          ];
          return (
            <TreeBranch
              key={app.key}
              label={app.display_name || app.key}
              open={Boolean(open)}
              expanded={expanded}
              icon={Layers}
              testId={`shell-nav-studio-app-${app.key}`}
              href={reviewHref}
              onSelect={() => selectApplication(app.key, chatAgentId)}
              onNavigate={closeMobile}
              onToggle={() => {
                setOpenAppKeys((current) => ({
                  ...current,
                  [app.key]: !open,
                }));
              }}
            >
              {appLeaves.map((leaf) => (
                <LeafLink
                  key={leaf.id}
                  item={leaf}
                  active={
                    applicationKey === app.key &&
                    isActivePath(leaf.href.split("?")[0])
                  }
                  expanded={expanded}
                  onNavigate={closeMobile}
                />
              ))}
            </TreeBranch>
          );
        })}
        {org ? (
          <CreateApplicationDialog
            open={createApplicationOpen}
            onOpenChange={setCreateApplicationOpen}
            organizationId={org}
          />
        ) : null}
      </TreeSection>

      {organizationLeaves.length > 0 ? (
        <TreeSection
          title={t("sidebar.organization")}
          open={orgOpen}
          onToggle={() => setOrgOpen((value) => !value)}
          expanded={expanded}
        >
          <div data-testid="shell-nav-studio-organization">
            {organizationLeaves.map((item) => (
              <LeafLink
                key={item.id}
                item={item}
                active={isActivePath(item.href.split("?")[0])}
                expanded={expanded}
                onNavigate={closeMobile}
              />
            ))}
          </div>
        </TreeSection>
      ) : null}
    </div>
  );
}
